import { TextField } from "@mui/material";

import { useDebouncedCallback } from "@/shared/hooks";
import { actions, useVizDispatch } from "@/shared/state/visualization";
import {
  PlotAxisConfig,
  PlotPanelConfig,
  PlotYAxis,
} from "@/shared/state/visualization/schema/v1";

import styles from "./AxisLabelInput.module.css";

interface AxisLabelInputProps {
  panelId: string;
  currentAxisConfig: PlotAxisConfig | undefined;
  axis: "x" | PlotYAxis;
}

export function AxisLabelInput({
  panelId,
  currentAxisConfig,
  axis,
}: AxisLabelInputProps) {
  const dispatch = useVizDispatch();

  const makeNewConfig = (
    axisConfig: Partial<PlotAxisConfig>,
  ): Partial<PlotPanelConfig> => {
    const config: Partial<PlotPanelConfig> = {};
    if (axis === "x") {
      config.xAxis = axisConfig;
    } else if (axis === "y") {
      config.yAxis = axisConfig;
    } else {
      config.extraYAxes = {
        [axis]: axisConfig,
      };
    }
    return config;
  };

  const handleLabelChange = useDebouncedCallback((newLabel: string) => {
    const config = makeNewConfig({ label: newLabel });
    dispatch(actions.setPlotPanelConfig(panelId, config));
  }, 300); // 300ms debounce delay chosen through trial and error

  let ariaLabel = axis === "x" ? "X-Axis Label" : "Y-Axis Label";
  if (axis.startsWith("y") && axis !== "y") {
    ariaLabel = `${ariaLabel} ${axis}`;
  }

  return (
    <TextField
      defaultValue={currentAxisConfig?.label}
      fullWidth
      label="Label"
      inputProps={{
        "aria-label": ariaLabel,
        className: styles.input,
        maxLength: 50, // 50 character limit chosen as a standard practice
      }}
      InputLabelProps={{
        className: styles.input,
      }}
      key={`${panelId}-${axis}-axis-label`}
      margin="dense"
      onChange={(e) => handleLabelChange(e.target.value)}
      placeholder="No label"
      size="small"
    />
  );
}
